import { RowClickedEvent } from '@ag-grid-enterprise/all-modules';
import { Box, makeStyles } from '@material-ui/core';
import { useParams } from '@reach/router';
import React, { useEffect, useRef } from 'react';

import TableGrid, { CellRenderer, MainColumns } from '../../../../../../components/TableGrid';
import TablePagination from '../../../../../../components/TableGrid/Pagination';
import { useTableGridApiState } from '../../../../../../components/TableGrid/TableGridApiContext/TableGridApiContext';
import { useErrorsDispatch } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext';
import { useBenchmarkDispatch, useBenchmarkState } from '../../../../Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../../../../Context/BenchmarkContext.types';
import { getDatasource } from '../../CompaniesMatrix/CompaniesMatrixTable';
import NavigationHeader from './NavigationHeader';

const SearchPanel: React.FC = () => {
  const classes = useStyles();
  const { pagination, companyFilter } = useBenchmarkState();
  const { tableGridApi } = useTableGridApiState();
  const dispatchBenchmark = useBenchmarkDispatch();
  const dispatchErrors = useErrorsDispatch();
  const { benchmarkId } = useParams();

  const prevFilterRef = useRef(companyFilter);
  useEffect(() => {
    if (prevFilterRef.current !== companyFilter && tableGridApi) {
      (tableGridApi as any).purgeServerSideCache();
    }
    prevFilterRef.current = companyFilter;
  }, [companyFilter, tableGridApi]);

  const onRowClicked = (event: RowClickedEvent) => {
    if (event?.data?.companyInfo?.id) {
      dispatchBenchmark({
        type: BenchmarkContextActionTypes.SetActiveBenchmarkCompanyId,
        payload: event?.data?.companyInfo.id,
      });
    }
  };

  return (
    <Box className={classes.searchPanelBox}>
      <NavigationHeader />
      <TableGrid
        translationKey="companies:companyInfo"
        columns={columnDefs}
        getDatasource={getDatasource()}
        isShowSideBar={false}
        suppressTableContextProvider={true}
        originalProps={{
          context: {
            pagination,
            benchmarkId,
            companyFilter,
            dispatchBenchmark,
            dispatchErrors,
          },
          headerHeight: 0,
          rowModelType: 'serverSide',
          pagination: true,
          paginationPageSize: pagination.size,
          cacheBlockSize: pagination.size,
          maxBlocksInCache: 1,
          suppressPaginationPanel: true,
          rowSelection: 'single',
          rowMultiSelectWithClick: false,
          defaultColDef: defaultColumnProp,
          onRowClicked: onRowClicked,
        }}
      >
        <TablePagination
          gridApi={tableGridApi}
          pagination={pagination}
          onChange={(value: number) => {
            dispatchBenchmark({
              type: BenchmarkContextActionTypes.SetPagination,
              payload: { ...pagination, page: value },
            });
          }}
        />
      </TableGrid>
    </Box>
  );
};

const defaultColumnProp = {
  resizable: true,
  flex: 1,
  minWidth: 100,
  menuTabs: [],
  sortable: false,
};

const columnDefs: MainColumns[] = [
  {
    headerName: '',
    field: 'companyInfo.name',
    minWidth: 300,
    cellClass: 'companyTitle',
    cellRenderer: CellRenderer.BenchmarkCompanyLink,
  },
];

const useStyles = makeStyles((theme) => ({
  searchPanelBox: {
    display: 'flex',
    flexDirection: 'column',
    width: '340px',
    paddingLeft: theme.spacing(4),
    '& .ag-row-selected': {
      backgroundColor: theme.palette.primary.main,
      '& .companyTitle': {
        color: theme.palette.background.paper,
      },
    },
    '& .ag-root-wrapper-body': {
      height: '100%',
    },
    '& .ag-theme-alpine .ag-header': {
      borderBottom: 'none',
    },
    marginRight: theme.spacing(8),
  },
}));
export default SearchPanel;
